import { useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { LoadingState, ErrorState } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { useAuth } from "@/lib/auth-context";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { ROLE_DISPLAY_NAMES } from "@platform/shared";
import type { Organization, OrganizationRole, AutomationSettingsDTO } from "@platform/shared";

interface Member {
  userId: string;
  email: string;
  fullName: string;
  role: OrganizationRole;
  memberSince: string;
}

interface NotificationSettings {
  emailOnNewLead?: boolean;
  emailOnMissedCall?: boolean;
  smsOnUrgent?: boolean;
}

export function Settings() {
  const { user, organizations } = useAuth();
  const canEdit = ["owner", "admin"].includes(organizations[0]?.role ?? "");

  const orgState = useApi<{ organization: Organization; role: OrganizationRole }>("/api/organizations/me");
  const membersState = useApi<{ members: Member[] }>("/api/organizations/me/members");
  const settingsState = useApi<{ settings: AutomationSettingsDTO }>("/api/automation/settings");

  const [businessName, setBusinessName] = useState("");
  const [nameSaveState, setNameSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [nameError, setNameError] = useState<string | null>(null);

  const [notifications, setNotifications] = useState<NotificationSettings>({});
  const [notifSaveState, setNotifSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    if (orgState.data) setBusinessName(orgState.data.organization.name);
  }, [orgState.data]);

  useEffect(() => {
    if (settingsState.data) {
      setNotifications((settingsState.data.settings.notificationSettings as NotificationSettings | null) ?? {});
    }
  }, [settingsState.data]);

  async function saveBusinessName() {
    setNameSaveState("saving");
    setNameError(null);
    try {
      await apiFetch("/api/organizations/me", { method: "PATCH", body: JSON.stringify({ name: businessName }) });
      setNameSaveState("saved");
      orgState.refetch();
      setTimeout(() => setNameSaveState("idle"), 2000);
    } catch (err) {
      setNameSaveState("error");
      setNameError(err instanceof ApiRequestError ? err.message : "Couldn't save.");
    }
  }

  async function saveNotifications(next: NotificationSettings) {
    setNotifications(next);
    setNotifSaveState("saving");
    try {
      await apiFetch("/api/automation/settings", {
        method: "PATCH",
        body: JSON.stringify({ notificationSettings: next }),
      });
      setNotifSaveState("saved");
      setTimeout(() => setNotifSaveState("idle"), 1500);
    } catch {
      setNotifSaveState("error");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Settings</h1>
        <p className="mt-1 text-sm text-ink-soft">Manage your organization's details.</p>
      </div>

      {/* Business information */}
      <Card className="max-w-lg p-6">
        <p className="mb-4 font-medium text-ink">Business information</p>
        {orgState.isLoading && <LoadingState />}
        {orgState.error && <ErrorState message={orgState.error} onRetry={orgState.refetch} />}
        {orgState.data && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-ink">Business name</label>
              <input
                disabled={!canEdit}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="mt-1 w-full rounded-md border border-line bg-paper-raised px-3 py-2 text-sm text-ink disabled:bg-paper disabled:text-ink-soft"
              />
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button onClick={saveBusinessName} disabled={nameSaveState === "saving"}>
                  {nameSaveState === "saving" ? "Saving…" : "Save changes"}
                </Button>
                {nameSaveState === "saved" && <span className="text-sm text-success-500">Saved</span>}
              </div>
            )}
            {nameError && <p className="text-sm text-signal-600">{nameError}</p>}
            <p className="text-xs text-ink-soft">
              Services, hours, and AI context live under Automation → Business information.
            </p>
          </div>
        )}
      </Card>

      {/* Account */}
      <Card className="max-w-lg p-6">
        <p className="mb-4 font-medium text-ink">Account</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between border-b border-line py-2">
            <span className="text-ink-soft">Name</span>
            <span className="font-medium text-ink">{user?.fullName}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-ink-soft">Email</span>
            <span className="font-medium text-ink">{user?.email}</span>
          </div>
        </div>
      </Card>

      {/* Team members */}
      <Card className="max-w-lg overflow-hidden">
        <div className="border-b border-line px-6 py-4">
          <p className="font-medium text-ink">Team members</p>
        </div>
        {membersState.isLoading && (
          <div className="px-6 py-4">
            <LoadingState />
          </div>
        )}
        {membersState.error && (
          <div className="px-6 py-4">
            <ErrorState message={membersState.error} onRetry={membersState.refetch} />
          </div>
        )}
        {membersState.data && (
          <ul className="divide-y divide-line">
            {membersState.data.members.map((m) => (
              <li key={m.userId} className="flex items-center justify-between px-6 py-3 text-sm">
                <div>
                  <p className="font-medium text-ink">{m.fullName}</p>
                  <p className="text-ink-soft">{m.email}</p>
                </div>
                <Badge tone="neutral">{ROLE_DISPLAY_NAMES[m.role]}</Badge>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-line px-6 py-3">
          <Button variant="secondary" disabled>
            Invite member (coming soon)
          </Button>
        </div>
      </Card>

      {/* Notification preferences */}
      <Card className="max-w-lg p-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-medium text-ink">Notification preferences</p>
          {notifSaveState === "saved" && <span className="text-xs text-success-500">Saved</span>}
        </div>
        {settingsState.isLoading && <LoadingState />}
        {settingsState.data && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={notifications.emailOnNewLead ?? false}
                onChange={(e) => saveNotifications({ ...notifications, emailOnNewLead: e.target.checked })}
              />
              Email when a new lead comes in
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={notifications.emailOnMissedCall ?? false}
                onChange={(e) => saveNotifications({ ...notifications, emailOnMissedCall: e.target.checked })}
              />
              Email on every missed call
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={notifications.smsOnUrgent ?? false}
                onChange={(e) => saveNotifications({ ...notifications, smsOnUrgent: e.target.checked })}
              />
              Text me for urgent/needs-human leads
            </label>
          </div>
        )}
      </Card>
    </div>
  );
}
